/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
*/

import { TalerFormAttributes } from "@gnu-taler/taler-util";
import { format } from "date-fns";
import { InternationalizationAPI } from "../../context/translation.js";
import { DoubleColumnFormDesign, FormMetadata } from "../forms-types.js";
import { vqfIdentityList, yesNoChoices } from "./VQF_902_12_13_common.js";
import { vqfLanguagePolicy } from "./vqf.js";

export const form_vqf_902_12 = (
  i18n: InternationalizationAPI,
): FormMetadata => ({
  label: i18n.fixed({
    en: "Foundations (as well as similar constructs) (S)",
    de: "Stiftungen (sowie ähnliche Konstrukte) (S)",
  }),
  description: i18n.fixed({
    en: "Declaration for foundations and similar constructs.",
    de: "Erklärung über Stiftungen und ähnliche Konstrukte.",
  }),
  id: "vqf_902_12",
  version: 1,
  config: VQF_902_12(i18n),
});

export function VQF_902_12(
  i18n: InternationalizationAPI,
): DoubleColumnFormDesign {
  const today = format(new Date(), "yyyy-MM-dd");
  return {
    type: "double-column",
    languagePolicy: vqfLanguagePolicy(i18n),
    title: i18n.fixed({
      en: "Foundations (as well as similar constructs) (S)",
      de: "Stiftungen (sowie ähnliche Konstrukte) (S)",
    }),
    sections: [
      {
        title: i18n.fixed({
          en: "Contracting partner and foundation",
          de: "Vertragspartner und Stiftung",
        }),
        fields: [
          {
            id: TalerFormAttributes.IDENTITY_CONTRACTING_PARTNER,
            label: i18n.fixed({
              en: "Contracting partner",
              de: "Vertragspartner",
            }),
            type: "textArea",
            required: true,
          },
          {
            type: "caption",
            label: i18n.fixed({
              en: "The undersigned hereby declare(s) that as board member of the foundation, or of the highest supervisory body of an underlying company of a foundation, known as:",
              de: "Der/die Unterzeichnete(n) erklärt/erklären hiermit, dass er/sie Mitglied des Stiftungsrates oder des obersten Aufsichtsorgans einer unterliegenden Gesellschaft einer Stiftung, mit dem Namen/der Firma:",
            }),
          },
          {
            id: TalerFormAttributes.LEGAL_STRUCTURE_NAME,
            label: i18n.fixed({
              en: "Name of the foundation",
              de: "Name/Firma der Stiftung",
            }),
            type: "text",
            required: true,
          },
          {
            type: "caption",
            label: i18n.fixed({
              en: "and, in such capacity, provide(s) to the best of his/her/their knowledge the following:",
              de: "ist/sind und in dieser Funktion nach seinem/ihrem besten Wissen die folgenden Informationen übermittelt/übermitteln:",
            }),
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Name and information pertaining to the foundation",
          de: "Namen und Angaben zur Stiftung",
        }),
        fields: [
          {
            id: TalerFormAttributes.LEGAL_STRUCTURE_TYPE,
            label: i18n.fixed({
              en: "Type of foundation",
              de: "Art der Stiftung",
            }),
            type: "choiceHorizontal",
            required: true,
            choices: [
              {
                value: "DISCRETIONARY",
                label: i18n.fixed({
                  en: "Discretionary foundation",
                  de: "Diskretionäre Stiftung",
                }),
              },
              {
                value: "NON_DISCRETIONARY",
                label: i18n.fixed({
                  en: "Non-discretionary foundation",
                  de: "Nicht-diskretionäre Stiftung",
                }),
              },
            ],
          },
          {
            id: TalerFormAttributes.LEGAL_STRUCTURE_REVOCABILITY,
            label: i18n.fixed({ en: "Revocability", de: "Widerrufbarkeit" }),
            type: "choiceHorizontal",
            required: true,
            choices: [
              {
                value: "REVOCABLE",
                label: i18n.fixed({
                  en: "Revocable foundation",
                  de: "Widerrufbare Stiftung",
                }),
              },
              {
                value: "IRREVOCABLE",
                label: i18n.fixed({
                  en: "Irrevocable foundation",
                  de: "Nicht-widerrufbare Stiftung",
                }),
              },
            ],
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Information pertaining to the founder",
          de: "Angaben zum Errichter/Stifter",
        }),
        description: i18n.fixed({
          en: "Information pertaining to the ultimate economic, not fiduciary, founder (individual(s) or entity/entities).",
          de: "Angaben zum tatsächlichen, nicht treuhänderischen Errichter/Stifter (Einzelperson/en oder Gesellschaft/en).",
        }),
        fields: [
          vqfIdentityList(
            i18n,
            TalerFormAttributes.ORIGINATOR_LIST,
            i18n.fixed({ en: "Founder(s)", de: "Errichter/Stifter" }),
            true,
            true,
          ),
          {
            id: TalerFormAttributes.ORIGINATOR_HAS_REVOCATION_RIGHT,
            label: i18n.fixed({
              en: "Does the founder have the right to revoke the foundation?",
              de: "Hat der Stifter das Recht, die Stiftung zu widerrufen?",
            }),
            type: "choiceHorizontal",
            choices: yesNoChoices(i18n),
            required: true,
            hide(_value, root) {
              return (
                root[TalerFormAttributes.LEGAL_STRUCTURE_REVOCABILITY] !==
                "REVOCABLE"
              );
            },
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Founders of pre-existing foundations",
          de: "Errichter/Stifter vorbestehender Stiftungen",
        }),
        description: i18n.fixed({
          en: "Complete this section if the foundation results from a restructuring (re-settlement) or merger of pre-existing foundations.",
          de: "Auszufüllen, wenn die Stiftung aus einer Restrukturierung (Re-Settlement) oder Zusammenlegung (Merger) vorbestehender Stiftungen entstanden ist.",
        }),
        fields: [
          vqfIdentityList(
            i18n,
            TalerFormAttributes.PREDECESSOR_ORIGINATOR_LIST,
            i18n.fixed({
              en: "Founder(s) of the pre-existing foundation(s)",
              de: "Errichter/Stifter der vorbestehenden Stiftung(en)",
            }),
            true,
            false,
          ),
        ],
      },
      {
        title: i18n.fixed({
          en: "Beneficiaries",
          de: "Angaben zu Begünstigten",
        }),
        fields: [
          vqfIdentityList(
            i18n,
            TalerFormAttributes.BENEFICIARY_LIST,
            i18n.fixed({
              en: "Beneficiaries determined at the time this form is signed",
              de: "Im Zeitpunkt der Unterzeichnung bestimmte Begünstigte",
            }),
            false,
            false,
          ),
          {
            id: TalerFormAttributes.BENEFICIARIES_HAVE_DISTRIBUTION_CLAIM,
            label: i18n.fixed({
              en: "Does the beneficiary have an actual right to claim distributions?",
              de: "Hat der Begünstigte einen festen Anspruch auf Ausschüttungen?",
            }),
            type: "choiceHorizontal",
            choices: yesNoChoices(i18n),
            required: true,
            hide(_value, root) {
              return !root[TalerFormAttributes.BENEFICIARY_LIST]?.length;
            },
          },
          {
            id: TalerFormAttributes.BENEFICIARY_GROUPS,
            label: i18n.fixed({
              en: "Known group(s) of beneficiaries",
              de: "Bekannte Begünstigtengruppe(n)",
            }),
            help: i18n.fixed({
              en: "In addition to defined beneficiaries, or if no beneficiary has been defined (for example, descendants of the founder).",
              de: "Zusätzlich zu bestimmten Begünstigten oder falls keine bestimmten Begünstigten festgelegt wurden (z.B. Nachkommen des Errichters/Stifters).",
            }),
            type: "textArea",
            required: false,
            validator(text, form) {
              if (
                !text?.trim() &&
                !form[TalerFormAttributes.BENEFICIARY_LIST]?.length
              ) {
                return i18n.fixed({
                  en: "Provide at least one beneficiary or group of beneficiaries",
                  de: "Bitte geben Sie mindestens einen Begünstigten oder eine Begünstigtengruppe an.",
                });
              }
              return undefined;
            },
          },
        ],
      },
      {
        title: i18n.fixed({ en: "Further persons", de: "Weitere Personen" }),
        description: i18n.fixed({
          en: "Persons entitled to determine or nominate representatives of the foundation when those representatives may dispose of assets or change asset distributions or the nomination of beneficiaries.",
          de: "Personen, welche Vertreter der Stiftung bestimmen oder ernennen können, sofern diese Vertreter über Vermögenswerte verfügen oder die Vermögenszuteilung beziehungsweise Ernennung von Begünstigten ändern können.",
        }),
        fields: [
          vqfIdentityList(
            i18n,
            TalerFormAttributes.FURTHER_PERSON_LIST,
            i18n.fixed({ en: "Further person(s)", de: "Weitere Person(en)" }),
            false,
            false,
          ),
          {
            id: TalerFormAttributes.FURTHER_PERSON_HAS_REVOCATION_RIGHT,
            label: i18n.fixed({
              en: "Is/are there (a) further person(s) with the right to revoke the foundation?",
              de: "Hat/Haben diese weitere(n) Person(en) das Recht, die Stiftung zu widerrufen?",
            }),
            type: "choiceHorizontal",
            choices: yesNoChoices(i18n),
            required: true,
            hide(_value, root) {
              return (
                root[TalerFormAttributes.LEGAL_STRUCTURE_REVOCABILITY] !==
                "REVOCABLE"
              );
            },
          },
        ],
      },
      {
        title: i18n.fixed({ en: "Signature(s)", de: "Unterschrift(en)" }),
        description: i18n.fixed({
          en: "It is a criminal offence to deliberately provide false information on this form (article 251 of the Swiss Criminal Code, document forgery).",
          de: "Die vorsätzliche Angabe falscher Informationen in diesem Formular ist eine strafbare Handlung (Urkundenfälschung gemäss Artikel 251 des Schweizerischen Strafgesetzbuchs).",
        }),
        fields: [
          {
            type: "caption",
            label: i18n.fixed({
              en: "The contracting partner(s) undertake(s) to automatically inform of any changes to the information contained herein.",
              de: "Der/die Vertragspartner verpflichtet/verpflichten sich, allfällige Änderungen an den hier gemachten Angaben unverzüglich mitzuteilen.",
            }),
          },
          {
            id: TalerFormAttributes.SIGNATURE,
            label: i18n.fixed({ en: "Signature(s)", de: "Unterschrift(en)" }),
            type: "text",
            required: true,
          },
          {
            id: TalerFormAttributes.SIGN_DATE,
            label: i18n.fixed({ en: "Date", de: "Datum" }),
            type: "isoDateText",
            placeholder: "dd/MM/yyyy",
            pattern: "dd/MM/yyyy",
            defaultValue: today,
            disabled: true,
            required: true,
          },
        ],
      },
    ],
  };
}
